import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ExaminationFormResponse } from '@pixacare/dtos';
import { DialogService } from '@services/dialog.service';
import { ActionsService } from '@shared/models/helpers/actions-service';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { mapToArray } from '@shared/rx-pipes/map-to-array.pipe';
import { isEverythingTrue$ } from '@shared/utils/utils';
import { first, map, of, switchMap } from 'rxjs';
import { ExaminationSelectDialogComponent } from '../components/examination-select-dialog/examination-select-dialog.component';
import { ExaminationActionsService } from './examination-actions.service';
import { ExaminationsService } from './examinations.service';

@Injectable()
export class ExaminationMobileActionsService extends ActionsService {
  private readonly examinationsService = inject(ExaminationsService);
  private readonly examinationActionsService = inject(ExaminationActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);

  actions: MenuAction<number>[] = [
    {
      id: MenuActionId.EXAMINATION_CREATE,
      icon: '@tui.plus',
      label: this.translocoService.translate('Ajouter un examen'),
      condition: (count) => count === 0,
      condition$: () =>
        isEverythingTrue$(
          this.examinationsService.canWriteExamination$,
          this.examinationsService.examinationForms$.pipe(
            mapToArray(),
            map((examinationForms) => !!examinationForms.length),
          ),
        ),
      execute: (): void => {
        {
          this.examinationsService.examinationForms$
            .pipe(
              first(),
              mapToArray(),
              switchMap((examinationForms) => {
                if (!examinationForms.length) {
                  return null;
                }

                if (examinationForms.length === 1) {
                  return of(examinationForms[0]);
                }

                return this.dialogService.openComponent<ExaminationSelectDialogComponent, ExaminationFormResponse>(
                  ExaminationSelectDialogComponent,
                  {
                    data: {
                      examinationForms,
                      text: this.translocoService.translate("Sélectionner le type d'examen à ajouter"),
                    },
                    label: this.translocoService.translate('Sélectionner un examen'),
                    size: 'm',
                  },
                );
              }),
            )
            .subscribe((examinationForm) => {
              if (!examinationForm) {
                return;
              }

              this.examinationActionsService.addExamination(examinationForm);
            });
        }
      },
    },
    {
      id: MenuActionId.EXAMINATION_OPEN,
      label: this.translocoService.translate('Ouvrir'),
      icon: '@tui.maximize-2',
      condition: (count) => !!count,
      execute: (): void => {
        this.examinationsService.props$.pipe(first()).subscribe(({ clientCode, departmentId, patientId }) => {
          this.examinationActionsService.openExaminations({
            clientCode,
            departmentId,
            patientId,
          });
        });
      },
    },
  ];
}
